import ProductHeroMobileSlider, {
  PRODUCT_SLIDER_MOBILE_BREAKPOINTS,
} from '@/components/ProductHeroMobileSlider/ProductHeroMobileSlider'
import styles from './ProductPageMobile.module.scss'
import Link from '@/components/Link/Link'
import { DOC_TYPES, PRODUCTS_SLUG, TRANSITION_DURATION, TRANSITION_DURATION_MS } from '@/data'
import ProductPageUiInfoTable from '@/sections/ProductPageUi/ProductPageUiInfoTable/ProductPageUiInfoTable'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import AnimatedTextLines from '@/components/AnimatedTextLines/AnimatedTextLines'
import { DownIcon } from '@/sections/ProductPageUi/ProductPageUiLeftSide/ProductPageUiLeftSide'
import useStore from '@/store'
import classnames from 'classnames'
import gsap from 'gsap'
import { formatNumber, getProductLinkObject } from '@/utils'
import { INDEX_NUMBER_OF_DIGITS } from '@/sections/ProductPageUi/ProductPageUiMisc/ProductPageUiMisc'
import useInView from '@/hooks/use-in-view'
import SanityImage from '@/components/SanityImage/SanityImage'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import { ScrollContext } from '@/context/Scroll'

gsap.registerPlugin(ScrollTrigger)

const SLIDER_CLASS = styles.slider

const ProductPageMobile = product => {
  const infoTableRef = useRef(null)
  const productData = product.productData
  const textLinesRefs = useRef({
    downloadSpecSheetLabel: null,
    finishesLabel: null,
    descriptionTitle: null,
    descriptionLines: null,
    descriptionDescription: null,
    descriptionLine: null,
    closeText: null,
    styleText: null,
    numberRange: null,
  })
  const { setElementToObserve: setDescriptionElementToObserve, isInView: descriptionInView } = useInView()
  const hasAnimatedIn = useRef(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [allowInViewAnimation, setAllowInViewAnimation] = useState(false)
  const descriptionLineRefs = useRef({
    top: null,
    bottom: null,
  })
  const setProductPageData = useStore(state => state.setProductPageData)
  const setProductGalleryImagesOpen = useStore(state => state.setProductGalleryImagesOpen)
  const lengthOfImagesText = useMemo(
    () => `${formatNumber(productData?.images?.length, INDEX_NUMBER_OF_DIGITS)}`,
    [productData?.images?.length],
  )
  const { scroll } = useContext(ScrollContext)
  const $sliderContainer = useRef(null)
  const loaderAnimationComplete = useStore(state => state.loaderAnimationComplete)

  useEffect(() => {
    if (!product) return
    setProductPageData(product)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product])

  useEffect(() => {
    if (!textLinesRefs.current.numberRange) return
    textLinesRefs.current.numberRange.updateText(
      `${formatNumber(activeIndex + 1, INDEX_NUMBER_OF_DIGITS)} - ${lengthOfImagesText}`,
    )
  }, [activeIndex, lengthOfImagesText])

  const animateInDescription = () => {
    Object.keys(descriptionLineRefs.current).forEach((key, index) => {
      if (!descriptionLineRefs.current[key]) return
      gsap.to(descriptionLineRefs.current[key], {
        scaleX: 1,
        duration: 0.9,
        delay: 0.2 * index,
        ease: 'power3.easeOut',
      })
    })

    if (textLinesRefs.current.descriptionTitle) {
      textLinesRefs.current.descriptionTitle.animate()
    }

    if (textLinesRefs.current.descriptionLines) {
      textLinesRefs.current.descriptionLines.animate()
    }
  }

  const animateIn = () => {
    const sliderDuration = TRANSITION_DURATION * 0.65

    if ($sliderContainer.current) {
      gsap.to($sliderContainer.current, {
        '--left-y': '0%',
        '--right-y': '0%',
        duration: sliderDuration,
        delay: TRANSITION_DURATION * 0.2,
        ease: 'Power3.easeInOut',
      })
    }

    const sliderCarousel = $sliderContainer.current.querySelector(`.${SLIDER_CLASS}`)
    if (sliderCarousel) {
      gsap.to(sliderCarousel, {
        scale: 1,
        duration: sliderDuration,
        delay: TRANSITION_DURATION * 0.2,
        ease: 'Power3.easeInOut',
      })
    }
    setTimeout(
      () => {
        if (infoTableRef.current) {
          infoTableRef.current.animateIn()
        }
      },
      TRANSITION_DURATION * 0.4 * 1000,
    )

    setTimeout(() => {
      if (textLinesRefs?.current?.closeText) {
        textLinesRefs.current.closeText.animate()
      }

      if (textLinesRefs?.current?.styleText) {
        textLinesRefs.current.styleText.animate()
      }

      if (textLinesRefs?.current?.numberRange) {
        textLinesRefs.current.numberRange.animate()
      }
    }, sliderDuration * 1000)
  }

  useEffect(() => {
    if (!loaderAnimationComplete) return
    if (!productData || hasAnimatedIn.current) return
    hasAnimatedIn.current = true
    setTimeout(() => {
      animateIn()
    }, 100)
    setTimeout(() => {
      setAllowInViewAnimation(true)
    }, TRANSITION_DURATION_MS)
  }, [productData, scroll, loaderAnimationComplete])

  useEffect(() => {
    if (!descriptionInView || !allowInViewAnimation) return
    animateInDescription()
  }, [descriptionInView, allowInViewAnimation])

  return (
    <div className={styles.ProductPageMobile}>
      <div className={styles.inner}>
        <div className={styles.topContent}>
          <div className={styles.topBar}>
            <Link
              className={styles.topBar__closeLink}
              link={{
                linkType: 'internal',
                link: {
                  _type: DOC_TYPES.PAGE,
                  slug: PRODUCTS_SLUG,
                },
              }}
            >
              <AnimatedTextLines
                autoAnimate={false}
                querySelector="span"
                ref={ref => {
                  textLinesRefs.current.closeText = ref
                }}
              >
                <span className={styles.topBar__closeText}>Close</span>
              </AnimatedTextLines>
            </Link>
            <AnimatedTextLines
              autoAnimate={false}
              querySelector="span"
              ref={ref => {
                textLinesRefs.current.styleText = ref
              }}
              className={styles.topBar__styleText}
            >
              <span className={styles.topBar__styleText__span}>Style</span>
              <span className={styles.topBar__styleText__span}>{productData?.productCategory?.title}</span>
            </AnimatedTextLines>
            <AnimatedTextLines
              autoAnimate={false}
              querySelector="span"
              ref={ref => {
                textLinesRefs.current.numberRange = ref
              }}
              className={styles.topBar__numberRange}
            >
              <span className={styles.topBar__numberRange__span}>
                {`${formatNumber(1, INDEX_NUMBER_OF_DIGITS)} - ${lengthOfImagesText}`}
              </span>
            </AnimatedTextLines>
          </div>
          <div
            className={styles.sliderContainer}
            ref={$sliderContainer}
          >
            <ProductHeroMobileSlider
              setActiveIndex={setActiveIndex}
              className={SLIDER_CLASS}
              images={productData.images.map(imageObject => imageObject?.image)}
            />
          </div>
          <ProductPageUiInfoTable
            className={styles.infoTable}
            ref={infoTableRef}
            productData={product}
          />
        </div>
        {(productData?.specSheet?.url || productData?.galleryImages?.length > 0) && (
          <div className={styles.downloadAndFinishesButtons}>
            {productData?.specSheet?.url && (
              <div className={styles.downloadSpecSheetLabelContainer}>
                <Link
                  className={classnames(styles.downloadSpecSheetLabel, styles.textLinesValue)}
                  link={{
                    linkType: 'external',
                    link: productData?.specSheet?.url,
                  }}
                >
                  <AnimatedTextLines
                    autoAnimate={true}
                    querySelector="span"
                    ref={ref => {
                      textLinesRefs.current.downloadSpecSheetLabel = ref
                    }}
                    className={classnames(styles.downloadSpecSheetLabel__lines, styles.textLines)}
                  >
                    <span className={classnames(styles.downloadSpecSheetLabel, styles.textLinesValue)}>
                      Download Spec Sheet <DownIcon />
                    </span>
                  </AnimatedTextLines>
                </Link>
              </div>
            )}
            {/* Finishes Label */}
            {productData?.galleryImages?.length > 0 && (
              <button
                onClick={() => {
                  setProductGalleryImagesOpen(true)
                }}
                className={styles.finishesLabelContainer}
              >
                <AnimatedTextLines
                  autoAnimate={true}
                  querySelector="span"
                  ref={ref => {
                    textLinesRefs.current.finishesLabel = ref
                  }}
                  className={classnames(styles.finishesLabel__lines, styles.textLines)}
                >
                  <span className={classnames(styles.finishesLabel, styles.textLinesValue)}>Finishes</span>
                </AnimatedTextLines>
              </button>
            )}
          </div>
        )}

        {productData?.description && (
          <div
            className={styles.description}
            ref={ref => {
              if (!ref) return
              ref.dataset.math = Math.random()
              setDescriptionElementToObserve(ref)
            }}
          >
            <AnimatedTextLines
              autoAnimate={allowInViewAnimation}
              querySelector="span"
              ref={ref => {
                textLinesRefs.current.descriptionTitle = ref
              }}
              className={styles.description__title}
            >
              <span className={styles.description__titleText}>info</span>
            </AnimatedTextLines>
            <div
              className={classnames(styles.description__line, styles.top)}
              ref={ref => {
                descriptionLineRefs.current.top = ref
              }}
            />
            <AnimatedTextLines
              querySelector="span"
              autoAnimate={allowInViewAnimation}
              ref={ref => {
                textLinesRefs.current.descriptionLines = ref
              }}
              className={styles.description__description}
            >
              <span className={styles.description__descriptionText}>{productData?.description}</span>
            </AnimatedTextLines>
            <div
              className={classnames(styles.description__line, styles.bottom)}
              ref={ref => {
                descriptionLineRefs.current.bottom = ref
              }}
            />
          </div>
        )}

        {product.nextProduct && (
          <div className={styles.nextProductContainer}>
            <Link
              link={getProductLinkObject(product.nextProduct.slug.current, product.nextProduct.title)}
              className={styles.nextProduct}
            >
              <AnimatedTextLines
                querySelector="p"
                autoAnimate={allowInViewAnimation}
                toAnimate="words"
                id="nextProduct"
              >
                <p className={styles.nextProductTitle}>Next Product</p>
              </AnimatedTextLines>
              <SanityImage
                image={product.nextProduct.image}
                breakpoints={PRODUCT_SLIDER_MOBILE_BREAKPOINTS}
                className={styles.nextProductImage}
                priority
                disableLoadedOpacity
              />
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

ProductPageMobile.displayName = 'ProductPageMobile'

export default ProductPageMobile
